import { useQuery } from "@apollo/client"
import React, { Dispatch, ReactNode, createContext, useContext, useEffect, useState } from "react"
import { GET_MY_USER_QUERY } from "../entities/users/graphql/queries"
import { MY_USER_SUBSCRIPTION } from "../entities/users/graphql/subscription"
import { IMyUser } from "../entities/users/interface"
import NativeLayerContext from "./NativeLayerContext"

export interface UserContextAction {
    type: string
    payload: IMyUser
}

interface UserContextType {
    user: IMyUser | null
    userLoading: boolean
    error: string | null
    setIsUserUpdate: React.Dispatch<React.SetStateAction<boolean>>
}

interface UserProviderProps {
    children: ReactNode
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const UserDispatchContext = createContext<Dispatch<UserContextAction>>(() => {})

export function UserProvider({ children }: UserProviderProps) {
    const nativeContext = useContext(NativeLayerContext)
    const [user, setUser] = useState<IMyUser | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isUserUpdate, setIsUserUpdate] = useState(false)
    const { data: myUserData, error: queryError, subscribeToMore, loading, refetch } = useQuery(GET_MY_USER_QUERY)

    subscribeToMore({
        document: MY_USER_SUBSCRIPTION,
        updateQuery: (prev: any, { subscriptionData }: { subscriptionData: any }) => {
            if (!subscriptionData.data) return prev
            return subscriptionData.data
        },
    })

    useEffect(() => {
        if (queryError) {
            setError(error)
        }
    }, [queryError])

    useEffect(() => {
        if (myUserData) {
            setUser(myUserData.myUser)
        }
    }, [myUserData])

    useEffect(() => {
        refetch()
        setIsUserUpdate(false)
    }, [nativeContext.auth0IdToken, isUserUpdate])

    const userContext = {
        user,
        userLoading: loading,
        error,
        setIsUserUpdate,
    }

    return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>
}
